/**
 * Doserne app-icon badge sync (installed PWA / mobile home-screen icon).
 *
 * Chrome/Edge/Android (and Safari 16.4+ once installed) support the
 * App Badging API — `navigator.setAppBadge(n)` shows a red dot with
 * the number `n` on the app icon on the phone home screen. The
 * service worker already bumps this counter every time a push
 * notification is delivered, so all we have to do here is CLEAR it
 * whenever a user is actually viewing the Doserne app.
 *
 * Behaviour:
 *   • On mount (whenever a Doserne page is opened): clear badge.
 *   • On tab focus / visibility change → visible: clear badge.
 *   • On unmount: no-op (leave whatever the SW has set later).
 *
 * The hook uses `postMessage` to the service worker so the SW can
 * clear its own cache-backed counter AS WELL AS the OS badge.
 * Fallback: also calls `navigator.clearAppBadge()` directly.
 */
import { useEffect } from "react";

async function clearBadge() {
  try {
    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "DOSERNE_CLEAR_BADGE" });
    } else if (navigator.serviceWorker) {
      const reg = await navigator.serviceWorker.ready;
      reg.active?.postMessage({ type: "DOSERNE_CLEAR_BADGE" });
    }
  } catch { /* silent */ }
  try {
    if (typeof navigator.clearAppBadge === "function") {
      await navigator.clearAppBadge();
    }
  } catch { /* silent */ }
}

export function useDoserneAppBadge() {
  useEffect(() => {
    // Clear on mount
    clearBadge();

    // Also clear whenever the tab becomes visible again
    const onVisibility = () => {
      if (document.visibilityState === "visible") clearBadge();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", clearBadge);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", clearBadge);
    };
  }, []);
}
